import React from "react";

import { SimpleDialog, SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import { Button } from "@/components/ui/button";
import { ClientFactory } from "@/main/common/ClientFactory";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import { getSafeToastMessageFromError, isExpectedUserFacingRejection } from "@/main/common/ApiUtils";
import { toast } from "sonner";
import type { Localizer } from "@/main/react/Types";
import logger from "@/lib/logger";
import ReviewFacetFields from "@/main/react/discussion/facet/ReviewFacetFields";
import {
    EMPTY_REVIEW_FACET_STATE,
    subjectSelectionFromDraft,
    type ReviewFacetState,
} from "@/main/react/discussion/facet/ReviewFacetTypes";

export type ReviewFacetDialogProps = {
    localizer: Localizer;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    discussionPublicId: string;
    /** The post's current review details, if it already carries the facet. */
    initialSubjectPublicId?: string;
    initialSubjectName?: string;
    initialRatingHalfStars?: number;
    /** Called after a successful save or removal, with the new values (or nulls after removal). */
    onSaved: (next: { subjectName?: string; subjectSlug?: string; ratingHalfStars?: number }) => void;
};

/**
 * Add, change or remove a published post's review details.
 *
 * <p>Wraps the SAME `SubjectCombobox` + `HalfStarRatingInput` pair the publish panel uses — one
 * component pair, two hosts. That is not merely economical: an author who set a rating in the composer
 * and later edits it should be operating the same control, not a second implementation of it that
 * rounds or validates slightly differently.
 *
 * <p>Every refusal comes from the SERVER and is surfaced verbatim, never guessed at locally. Two of
 * them cannot be evaluated on the client at all — whether the post is still publicly visible, and
 * whether it already carries the QUESTION facet — so a local pre-check would be a second, weaker copy
 * of a rule the server owns. The one thing this dialog decides for itself is which verb to send.
 */
/**
 * `warn` for a refusal the server meant to send, `error` for anything else. The distinction matters
 * beyond tidiness: `error` is forwarded to `/api/client-logs`, which is how a crashed island reports
 * itself, so a designed refusal logged at that level dilutes the one signal meaning the UI broke.
 */
function logIfUnexpected(error: unknown) {
    if (isExpectedUserFacingRejection(error)) {
        logger.warn(error);
        return;
    }
    logger.error(error);
}

export default function ReviewFacetDialog({
    localizer,
    open,
    onOpenChange,
    discussionPublicId,
    initialSubjectPublicId,
    initialSubjectName,
    initialRatingHalfStars,
    onSaved,
}: ReviewFacetDialogProps) {
    const [state, setState] = React.useState<ReviewFacetState>(EMPTY_REVIEW_FACET_STATE);
    const [saving, setSaving] = React.useState(false);

    const hadFacet = Boolean(initialSubjectPublicId && initialRatingHalfStars);

    // Re-seed each time the dialog opens, not once on mount: reopening it after a cancel must show the
    // post's persisted values again rather than whatever the author abandoned last time.
    React.useEffect(() => {
        if (!open) {
            return;
        }
        setState({
            subject: subjectSelectionFromDraft(initialSubjectPublicId, initialSubjectName),
            ratingHalfStars: initialRatingHalfStars ?? null,
        });
    }, [initialRatingHalfStars, initialSubjectName, initialSubjectPublicId, open]);

    const handleSave = React.useCallback(async () => {
        if (saving) {
            return;
        }
        setSaving(true);
        try {
            await ClientFactory.updateReviewFacet(discussionPublicId, {
                reviewSubjectPublicId: state.subject?.mode === "existing" ? state.subject.publicId : undefined,
                reviewSubjectName: state.subject?.mode === "new" ? state.subject.name : undefined,
                reviewRatingHalfStars: state.ratingHalfStars ?? undefined,
            });
            toast.success(localizer("discussion.facet.review.edit.saved_toast"));
            onSaved({
                subjectName: state.subject?.name,
                // The slug is minted server-side, so the caller reloads to pick it up. Reporting the
                // name immediately is what stops the strip flickering to empty in the meantime.
                subjectSlug: undefined,
                ratingHalfStars: state.ratingHalfStars ?? undefined,
            });
            onOpenChange(false);
        } catch (error) {
            if (isHandledAuthSessionError(error)) {
                return;
            }
            // A designed refusal (§9.7 visibility, the QUESTION conflict) is the feature working, not a
            // fault — and `logger.error` forwards to the same channel an island crash reports through.
            logIfUnexpected(error);
            // Surface the SERVER's message when it has one: "this post is set up as a question, switch
            // it back first" is the only useful thing to say, and only the server knows to say it.
            toast.error(getSafeToastMessageFromError(error, localizer("discussion.facet.review.edit.error")));
        } finally {
            setSaving(false);
        }
    }, [discussionPublicId, localizer, onOpenChange, onSaved, saving, state]);

    const handleRemove = React.useCallback(async () => {
        if (saving) {
            return;
        }
        setSaving(true);
        try {
            await ClientFactory.removeReviewFacet(discussionPublicId);
            toast.success(localizer("discussion.facet.review.edit.removed_toast"));
            onSaved({ subjectName: undefined, subjectSlug: undefined, ratingHalfStars: undefined });
            onOpenChange(false);
        } catch (error) {
            if (isHandledAuthSessionError(error)) {
                return;
            }
            // A designed refusal (§9.7 visibility, the QUESTION conflict) is the feature working, not a
            // fault — and `logger.error` forwards to the same channel an island crash reports through.
            logIfUnexpected(error);
            toast.error(getSafeToastMessageFromError(error, localizer("discussion.facet.review.edit.error")));
        } finally {
            setSaving(false);
        }
    }, [discussionPublicId, localizer, onOpenChange, onSaved, saving]);

    return (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={localizer("discussion.facet.review.edit.dialog_title")}
            loading={saving}
        >
            <div data-playwright-id="review-facet-dialog">
                <ReviewFacetFields
                    localizer={localizer}
                    value={state}
                    onChange={setState}
                    disabled={saving}
                />
            </div>

            <SimpleDialogFooter>
                {hadFacet ? (
                    <Button
                        type="button"
                        variant="ghost"
                        disabled={saving}
                        onClick={() => void handleRemove()}
                        data-playwright-id="review-facet-dialog-remove"
                    >
                        {localizer("discussion.facet.review.edit.remove")}
                    </Button>
                ) : null}
                <Button
                    type="button"
                    variant="outline"
                    disabled={saving}
                    onClick={() => onOpenChange(false)}
                    data-playwright-id="review-facet-dialog-cancel"
                >
                    {localizer("literal.cancel")}
                </Button>
                <Button
                    type="button"
                    disabled={saving}
                    onClick={() => void handleSave()}
                    data-playwright-id="review-facet-dialog-save"
                >
                    {localizer("literal.save")}
                </Button>
            </SimpleDialogFooter>
        </SimpleDialog>
    );
}
